import { useEffect, useState, type ReactNode } from "react";
import { Button } from "../ui/Button";
import { Notice } from "../ui/Notice";
import { Drawer } from "../ui/Overlay";
import { Segmented } from "../ui/Panel";
import { CheckIcon } from "../ui/Tag";
import { createMcpTokenApi, mcpTokensApi, revokeMcpTokenApi, type McpTokenRow } from "../../lib/api";
import { useT, type MsgKey } from "../../lib/i18n";
import { formatAt } from "../../lib/time";
import { useTz } from "../../lib/tz-context";

/**
 * 顶栏「MCP」：两个 tab（leo 2026-10-27：令牌都在管端管）。
 *   - 运营后台：src/admin/mcp-server.ts，令牌 amcp_；
 *   - 客户端联调：游戏 Worker 的 /mcp（src/mcp/server.ts），令牌 cmcp_，客户端同学用自己的管端账号登录来生成。
 *
 * 运营后台 tab：
 * 运营不会配置（leo 2026-10-26），所以整页只有一件主事：点「复制给 Claude」，粘贴到 Claude Code 发送。
 * 能做的不止卡包：专用工具之外，Claude 用 list_admin_apis / call_admin_api 改其它配置（权限表 src/admin/mcp-api-policy.ts）。
 * 其它接法与令牌管理收进折叠区，平时不占地方。令牌明文只在生成那一次回来，库里只存哈希。
 */
async function copyText(text: string): Promise<boolean> {
  /*
   * navigator.clipboard 只在「刚点过」且页面有权限时能写：先等服务端建完令牌再写，点击的授权已经过期；
   * 管端嵌在 super-admin 的 iframe 里时剪贴板权限也常被拦（NotAllowedError）。
   * 拦了就退到老办法：选中一个临时文本框再 execCommand("copy")。两种都不行回 false，界面亮出文本框让人再点一次
   */
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch { /* 退到下面 */ }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try { return document.execCommand("copy"); } catch { return false; } finally { ta.remove(); }
}

/** 复制后还能对 Claude 说的话：标题 + 原话 */
const EXAMPLES: Array<[MsgKey, MsgKey]> = [
  ["mcp.ex.importT", "mcp.ex.import"],
  ["mcp.ex.translateT", "mcp.ex.translate"],
  ["mcp.ex.rewardsT", "mcp.ex.rewards"],
  ["mcp.ex.dropsT", "mcp.ex.drops"],
  ["mcp.ex.shopT", "mcp.ex.shop"],
  ["mcp.ex.reviewT", "mcp.ex.review"],
];

function Fold({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <details className="group rounded-base border border-line-lighter">
      <summary className="flex cursor-pointer select-none items-center justify-between px-4 py-3 text-sm font-medium text-ink [&::-webkit-details-marker]:hidden">
        {title}
        <span className="text-xs text-ink-placeholder transition-transform group-open:rotate-90">›</span>
      </summary>
      <div className="flex flex-col gap-3 border-t border-line-lighter px-4 py-3">{children}</div>
    </details>
  );
}

type Tab = "admin" | "client";
const TAB_KEY = "mcp.tab";

export function McpDrawer({ a0, onClose }: { a0: string | null; onClose: () => void }) {
  const t = useT();
  const [tab, setTab] = useState<Tab>(() => {
    try { return localStorage.getItem(TAB_KEY) === "client" ? "client" : "admin"; } catch { return "admin"; }
  });
  const pick = (v: Tab) => { setTab(v); try { localStorage.setItem(TAB_KEY, v); } catch { /* 无痕模式 */ } };
  return (
    <Drawer title={t("mcp.entry")} width={560} onClose={onClose}>
      <div className="mb-4">
        <Segmented<Tab> value={tab} onChange={pick} options={[{ value: "admin", label: t("mcp.tabAdmin") }, { value: "client", label: t("mcp.tabClient") }]} />
      </div>
      {tab === "admin" ? <AdminMcp a0={a0} /> : <ClientMcp />}
    </Drawer>
  );
}

function AdminMcp({ a0 }: { a0: string | null }) {
  const t = useT();
  const tz = useTz();
  const [data, setData] = useState<{ enabled: boolean; endpoint: string; env?: "test" | "prod"; tokens: McpTokenRow[] } | null>(null);
  const [err, setErr] = useState("");
  const [fresh, setFresh] = useState<{ token: string; endpoint: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  /** 一键复制的整段文案：自动复制被浏览器拦了也能在框里手动复制 */
  const [oneText, setOneText] = useState("");
  const [showText, setShowText] = useState(false);

  const load = () => mcpTokensApi("admin").then(setData).catch((e) => setErr((e as Error).message));
  useEffect(() => { void load(); }, []);

  const flash = (key: string) => { setCopied(key); setTimeout(() => setCopied((k) => (k === key ? "" : k)), 1800); };
  const copy = (key: string, text: string) => {
    void copyText(text).then((ok) => { if (ok) flash(key); else { setErr(t("mcp.copyBlocked")); setShowText(true); } });
  };

  // 测试 / 生产各一个名字：两个能同时装，工具名里带着环境，生产的不进免确认名单
  const isProd = data?.env === "prod";
  const serverName = `idol-admin-${isProd ? "prod" : "test"}`;
  const oneClick = async () => {
    setBusy(true); setErr("");
    try {
      const r: { token: string; endpoint: string } = fresh ?? await createMcpTokenApi(t("mcp.defaultNote"), "admin").then((x) => ({ token: x.token, endpoint: x.endpoint }));
      setFresh(r);
      void load();
      const cmd = `claude mcp add --scope user --transport http ${serverName} "${r.endpoint}?name=${serverName}" --header "Authorization: Bearer ${r.token}"`;
      const setup = t(isProd ? "mcp.setupProd" : "mcp.setupTest", { name: serverName });
      const text = t("mcp.oneClickText", { cmd, setup, name: serverName, envLabel: t(isProd ? "mcp.envProd" : "mcp.envTest"), a0: a0 ?? t("mcp.a0Unknown") });
      setOneText(text);
      if (await copyText(text)) flash("oneClick");
      else { setErr(t("mcp.copyBlocked")); setShowText(true); }
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const revoke = async (id: string) => {
    setErr("");
    try { await revokeMcpTokenApi(id); if (fresh?.token.startsWith(`amcp_${id}_`)) { setFresh(null); setOneText(""); } await load(); }
    catch (e) { setErr((e as Error).message); }
  };

  const endpoint = fresh?.endpoint ?? data?.endpoint ?? `${location.origin}/admin/api/mcp`;
  const tokenText = fresh?.token ?? t("mcp.tokenPh");
  const active = (data?.tokens ?? []).filter((x) => x.revoked_at_ms == null && x.expires_at_ms > Date.now());
  const done = copied === "oneClick";

  const Line = ({ k, text }: { k: string; text: string }) => (
    <div className="flex items-start gap-2">
      <code className="min-w-0 flex-1 break-all rounded-sm bg-fill-light px-2.5 py-1.5 font-mono text-xs text-ink">{text}</code>
      <Button size="sm" onClick={() => copy(k, text)}>{copied === k ? t("mcp.copied") : t("mcp.copy")}</Button>
    </div>
  );

  return (
      <div className="flex flex-col gap-4 text-sm leading-relaxed text-ink-regular">
        {data && !data.enabled && <Notice tone="warning">{t("mcp.disabled")}</Notice>}
        {err && <Notice tone="danger">{err}</Notice>}
        {data?.env && <Notice tone={isProd ? "danger" : "info"}>{t(isProd ? "mcp.envBannerProd" : "mcp.envBannerTest", { name: serverName })}</Notice>}

        <section className="flex flex-col items-center gap-3 rounded-base border border-primary-100 bg-primary-50 px-6 py-7 text-center">
          <h3 className="text-lg font-semibold text-ink">{t("mcp.title")}</h3>
          <p className="max-w-[440px] text-ink-secondary">{t("mcp.sub")}</p>
          <Button tone={done ? "success" : "primary"} disabled={busy || !data?.enabled} onClick={oneClick} className="mt-1 h-10 px-6 text-base">
            {busy ? "…" : done ? t("mcp.oneClickDone") : t("mcp.oneClick")}
          </Button>
          {oneText && (
            <button type="button" onClick={() => setShowText((v) => !v)} className="text-xs text-primary hover:underline">
              {showText ? t("mcp.hideText") : t("mcp.showText")}
            </button>
          )}
          {oneText && showText && (
            <div className="flex w-full items-start gap-2 text-left">
              <textarea readOnly value={oneText} rows={7} onFocus={(e) => e.currentTarget.select()}
                className="min-w-0 flex-1 rounded-sm border border-line bg-white px-2.5 py-1.5 font-mono text-xs text-ink" />
              <Button size="sm" tone="primary" onClick={() => copy("oneClick", oneText)}>{done ? t("mcp.copied") : t("mcp.copy")}</Button>
            </div>
          )}
        </section>

        <section className="flex flex-col gap-2">
          <h4 className="text-xs font-medium uppercase tracking-wide text-ink-placeholder">{t("mcp.exTitle")}</h4>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {EXAMPLES.map(([title, text]) => (
              <button
                key={text} type="button" onClick={() => copy(text, t(text))}
                className="flex flex-col gap-1 rounded-base border border-line-lighter bg-white p-3 text-left transition-colors hover:border-primary-100 hover:bg-primary-50"
              >
                <span className="flex items-center justify-between gap-2 font-medium text-ink">
                  {t(title)}
                  <span className="flex-none text-xs font-normal text-ink-placeholder">{copied === text ? <CheckIcon ok size={13} /> : t("mcp.copy")}</span>
                </span>
                <span className="line-clamp-2 text-xs text-ink-secondary">{t(text)}</span>
              </button>
            ))}
          </div>
        </section>

        <Fold title={t("mcp.more")}>
          <div className="font-medium text-ink">{t("mcp.desktop")}</div>
          <p className="text-xs text-ink-secondary">{t("mcp.desktopSteps")}</p>
          <Line k="desktop" text={`${endpoint}/t/${tokenText}?name=${serverName}`} />
          <div className="mt-1 font-medium text-ink">{t("mcp.code")}</div>
          <p className="text-xs text-ink-secondary">{t("mcp.codeSteps")}</p>
          <Line k="code" text={`claude mcp add --scope user --transport http ${serverName} "${endpoint}?name=${serverName}" --header "Authorization: Bearer ${tokenText}"`} />
          {!fresh && <p className="text-xs text-ink-placeholder">{t("mcp.placeholderTip")}</p>}
        </Fold>

        <Fold title={t("mcp.tokens", { n: active.length })}>
          {active.length === 0 && <p className="text-xs text-ink-placeholder">{t("mcp.noTokens")}</p>}
          {active.map((x) => (
            <div key={x.id} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-xs text-ink">amcp_{x.id}_…</div>
                <div className="text-xs text-ink-placeholder">
                  {t("mcp.lastUsed")}{x.last_used_at_ms ? formatAt(x.last_used_at_ms, tz) : "—"} · {t("mcp.expires")}{formatAt(x.expires_at_ms, tz)}
                </div>
              </div>
              <Button size="sm" tone="danger" onClick={() => revoke(x.id)}>{t("mcp.revoke")}</Button>
            </div>
          ))}
        </Fold>

        <p className="text-xs text-ink-placeholder">{t("mcp.safety")}</p>
      </div>
  );
}

/**
 * 客户端联调 tab：客户端同学用自己的管端账号登录，生成 cmcp_ 令牌接到自己的 Claude Code / Codex。
 * 接入后怎么查（search_docs、get_endpoint、call_api、report_feedback）写在 MCP 自己的使用说明里，这里不重复。
 */
function ClientMcp() {
  const t = useT();
  const tz = useTz();
  const [data, setData] = useState<{ enabled: boolean; endpoint: string; tokens: McpTokenRow[] } | null>(null);
  const [err, setErr] = useState("");
  const [fresh, setFresh] = useState<{ token: string; endpoint: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");

  const load = () => mcpTokensApi("client").then(setData).catch((e) => setErr((e as Error).message));
  useEffect(() => { void load(); }, []);
  const copy = (key: string, text: string) => {
    void copyText(text).then((ok) => {
      if (!ok) { setErr(t("mcp.copyBlocked")); return; }
      setCopied(key); setTimeout(() => setCopied((k) => (k === key ? "" : k)), 1800);
    });
  };
  const create = async () => {
    setBusy(true); setErr("");
    try {
      const r = await createMcpTokenApi(t("mcp.defaultNote"), "client");
      setFresh({ token: r.token, endpoint: r.endpoint });
      void load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const revoke = async (id: string) => {
    setErr("");
    try { await revokeMcpTokenApi(id); if (fresh?.token.startsWith(`cmcp_${id}_`)) setFresh(null); await load(); }
    catch (e) { setErr((e as Error).message); }
  };

  const endpoint = fresh?.endpoint ?? data?.endpoint ?? "";
  const token = fresh?.token ?? t("mcp.tokenPh");
  const claudeCmd = `claude mcp add --scope user --transport http aeroplane-test ${endpoint} --header "Authorization: Bearer ${token}"`;
  const codexCfg = `[mcp_servers.aeroplane-test]\nurl = "${endpoint}"\nhttp_headers = { Authorization = "Bearer ${token}" }`;
  const active = (data?.tokens ?? []).filter((x) => x.revoked_at_ms == null && x.expires_at_ms > Date.now());

  const Block = ({ k, title, text }: { k: string; title: string; text: string }) => (
    <div className="flex flex-col gap-1.5">
      <div className="font-medium text-ink">{title}</div>
      <div className="flex items-start gap-2">
        <pre className="min-w-0 flex-1 whitespace-pre-wrap break-all rounded-sm bg-fill-light px-2.5 py-1.5 font-mono text-xs text-ink">{text}</pre>
        <Button size="sm" disabled={!fresh} onClick={() => copy(k, text)}>{copied === k ? t("mcp.copied") : t("mcp.copy")}</Button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4 text-sm leading-relaxed text-ink-regular">
      {data && !data.enabled && <Notice tone="warning">{t("mcp.clientDisabled")}</Notice>}
      {err && <Notice tone="danger">{err}</Notice>}

      <section className="flex flex-col items-center gap-3 rounded-base border border-primary-100 bg-primary-50 px-6 py-7 text-center">
        <h3 className="text-lg font-semibold text-ink">{t("mcp.clientTitle")}</h3>
        <p className="max-w-[440px] text-ink-secondary">{t("mcp.clientSub")}</p>
        <Button tone={fresh ? "success" : "primary"} disabled={busy || !data?.enabled || !!fresh} onClick={create} className="mt-1 h-10 px-6 text-base">
          {busy ? "…" : fresh ? t("mcp.clientCreated") : t("mcp.clientCreate")}
        </Button>
      </section>

      {fresh && <Notice tone="warning">{t("mcp.clientOnce")}</Notice>}
      <Block k="claude" title={t("mcp.clientClaude")} text={claudeCmd} />
      <Block k="codex" title={t("mcp.clientCodex")} text={codexCfg} />
      {!fresh && <p className="text-xs text-ink-placeholder">{t("mcp.clientPlaceholderTip")}</p>}

      <Fold title={t("mcp.tokens", { n: active.length })}>
        {active.length === 0 && <p className="text-xs text-ink-placeholder">{t("mcp.clientNoTokens")}</p>}
        {active.map((x) => (
          <div key={x.id} className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-xs text-ink">cmcp_{x.id}_…</div>
              <div className="text-xs text-ink-placeholder">
                {t("mcp.lastUsed")}{x.last_used_at_ms ? formatAt(x.last_used_at_ms, tz) : "—"} · {t("mcp.expires")}{formatAt(x.expires_at_ms, tz)}
              </div>
            </div>
            <Button size="sm" tone="danger" onClick={() => revoke(x.id)}>{t("mcp.revoke")}</Button>
          </div>
        ))}
      </Fold>

      <p className="text-xs text-ink-placeholder">{t("mcp.clientSafety")}</p>
    </div>
  );
}
